/**
 * Request 1245 — the browser's GPS position for sign-in. The browser asks the person first; if they say no, or the device can't
 * tell, sign-in simply goes ahead without a location.
 */
export interface GeoPoint {
  lat: number;
  lng: number;
  accuracy: number | null;
  /** request 1267: the device's UTC offset in minutes, so the server can run the same sanity check */
  tz?: number;
}

/**
 * Request 1267 — a sanity check on what the device reports. Laptops without GPS guess their position from nearby Wi-Fi, and
 * that guess can be wildly off (a Sharjah office once came back as Perth, Australia). A real position sits roughly where the
 * device's own clock says it is: each hour of UTC offset is ~15° of longitude, so anything more than ~35° away from the time
 * zone is treated as unreliable and not recorded. Someone travelling has their clock change too, so real trips still pass.
 */
export function plausibleForTimeZone(p: { lng: number }, offsetMinutes = -new Date().getTimezoneOffset()): boolean {
  const expected = (offsetMinutes / 60) * 15;
  const diff = Math.abs((((p.lng - expected) % 360) + 540) % 360 - 180);
  return diff <= 35;
}

let latest: GeoPoint | null = null;
let pending: Promise<GeoPoint | null> | null = null;

/** starts (or reuses) a position lookup; resolves null when it isn't available within `waitMs` */
export function locate(waitMs = 8000): Promise<GeoPoint | null> {
  if (latest) return Promise.resolve(latest);
  if (typeof navigator === "undefined" || !navigator.geolocation) return Promise.resolve(null);
  if (!pending) {
    pending = new Promise<GeoPoint | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (p) => {
          const point = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy ?? null, tz: -new Date().getTimezoneOffset() };
          resolve(plausibleForTimeZone(point) ? (latest = point) : null); // request 1267: a position that can't be right is dropped
        },
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 5 * 60 * 1000 }
      );
    }).finally(() => {
      pending = null;
    });
  }
  return Promise.race([pending, new Promise<null>((r) => setTimeout(() => r(null), waitMs))]);
}

/** the position found so far (sent with each sign-in step) */
export const signInLocation = () => latest;

/** true when the person has already said yes, so asking again won't pop up a prompt */
export async function locationAllowed(): Promise<boolean> {
  try {
    const s = await navigator.permissions?.query({ name: "geolocation" as PermissionName });
    return s?.state === "granted";
  } catch {
    return false;
  }
}
